import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Stack, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ScoreConfigIcon from 'components/Icons/scoreConfigIcon';
import { v4 as uuidV4 } from 'uuid';

// Redux
import { getDialogState } from 'store/selectors/dialogSelectors';
import { setDialog } from 'store/actions/dialogActions';

// Custom components
import CustomDialog from 'components/CustomDialog/customDialog';
import CustomDialogHeader from 'components/CustomDialog/DialogHeader/dialogHeader.cmp';
import IosSwitch from 'components/IosSwitch';
import CustomButton from 'components/Button/CustomButton';
import CSelect from 'components/Dropdown/CSelect';
import ColorPicker from 'components/ColorPicker/Score';
import CustomInput from 'components/Form/TextField/TextField.cmp';
import { ImportantUpdateNotification } from './ImportantUpdateNotification';

// Hooks
import {
  useOverlapDetection,
  useChangeTracking,
  useFormValidation,
} from './hooks';

// Utils
import DIALOGS from 'utils/dialogIds';

// Types
import { Threshold, ColorPickerState, DialogData } from './types';

// Constants
import {
  THRESHOLD_TYPE_OPTIONS,
  DEFAULT_COLORS,
  DEFAULT_RATINGS,
} from './constants';

// Styled components
import {
  ContentContainer,
  SectionContainer,
  ThresholdRow,
  ThresholdRowWrapper,
  EmptyStateContainer,
  HeaderContainer,
  ThresholdGrid,
  ColorButton,
  ColorPreview,
  AddThresholdButtonCnt,
  OverlapErrorContainer,
  DeleteButtonContainer,
} from './styles';

const ScoringConfigurations: React.FC = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(getDialogState);
  const { open, data }: { open?: boolean; data?: DialogData } =
    dialogState?.[DIALOGS.SCORING_CONFIGURATION] || {};
  const { checklist, toggleScoring } = data || {};

  // State management
  const [isScoring, setIsScoring] = useState<boolean>(
    checklist?.isScoring || false,
  );
  const [scoreConfig, setScoreConfig] = useState<Threshold[]>(
    checklist?.scoreConfig || [],
  );
  const [colorPickerOpen, setColorPickerOpen] = useState<ColorPickerState>({
    index: null,
    anchorEl: null,
  });

  // Custom hooks
  const { overlappingIds, detectOverlaps, hasOverlaps } = useOverlapDetection(
    scoreConfig,
    isScoring,
  );
  const { showImportantUpdate, resetChangeTracking, dismissNotification } =
    useChangeTracking(isScoring, scoreConfig, checklist);
  const { isValid, requiresTwoInputs } = useFormValidation(
    isScoring,
    scoreConfig,
    detectOverlaps,
  );

  // Event handlers
  const handleClose = useCallback(() => {
    resetChangeTracking();
    dispatch(
      setDialog({ open: false, dialogId: DIALOGS.SCORING_CONFIGURATION }),
    );
  }, [dispatch, resetChangeTracking]);

  const handleSave = useCallback(() => {
    // Check for overlaps before saving
    if (isScoring && scoreConfig.length > 1) {
      const overlaps = detectOverlaps();
      if (overlaps.size > 0) return;
    }

    // Clean up any NaN values before saving
    const cleanedScoreConfig = scoreConfig?.map((threshold) => ({
      ...threshold,
      value: Array.isArray(threshold.value)
        ? (threshold?.value?.map((val) => (isNaN(val) ? 0 : val)) as [
            number,
            number,
          ])
        : threshold?.value,
    }));

    data?.onSave?.({ isScoring, scoreConfig: cleanedScoreConfig });
    handleClose();
  }, [isScoring, scoreConfig, data, handleClose, detectOverlaps]);

  const handleIsScoringToggle = useCallback(() => {
    const newValue = !isScoring;
    setIsScoring(newValue);
    toggleScoring?.(newValue);

    // If enabling weighted scoring and no scoreconfig exist, add a default one
    if (newValue && scoreConfig.length === 0) {
      handleAddThreshold();
    }
  }, [isScoring, scoreConfig.length, toggleScoring]);

  const handleAddThreshold = useCallback(() => {
    const newThreshold: Threshold = {
      id: uuidV4(),
      color: DEFAULT_COLORS[(scoreConfig.length || 0) % DEFAULT_COLORS.length],
      rating:
        DEFAULT_RATINGS[(scoreConfig.length || 0) % DEFAULT_RATINGS.length] ||
        'Rating',
      logic: 'btw',
      value: [0, 0], // Empty values for 'btw' type
    };
    setScoreConfig((prev) => [...prev, newThreshold]);
  }, [scoreConfig.length]);

  const handleDeleteThreshold = useCallback((id: string) => {
    setScoreConfig((prev) => prev.filter((threshold) => threshold.id !== id));
  }, []);

  const handleThresholdChange = useCallback(
    (id: string, field: keyof Threshold, value: any) => {
      setScoreConfig((prev) =>
        prev.map((threshold) => {
          if (threshold.id === id) {
            if (field === 'logic') {
              const newValue =
                value === 'btw' ? ([0, 100] as [number, number]) : '0';
              return { ...threshold, [field]: value, value: newValue };
            }
            return { ...threshold, [field]: value };
          }
          return threshold;
        }),
      );
    },
    [],
  );

  const handleValueChange = useCallback(
    (id: string, newValue: string | [number, number]) => {
      setScoreConfig((prev) =>
        prev.map((threshold) =>
          threshold.id === id ? { ...threshold, value: newValue } : threshold,
        ),
      );
    },
    [],
  );

  const handleSingleValueChange = useCallback(
    (id: string, inputValue: string) => handleValueChange(id, inputValue),
    [handleValueChange],
  );

  const handleRangeValueChange = useCallback(
    (id: string, index: 0 | 1, inputValue: string) => {
      const threshold = scoreConfig.find((t) => t.id === id);
      if (threshold && Array.isArray(threshold.value)) {
        const newValue: [number, number] = [...threshold.value] as [
          number,
          number,
        ];
        // Store the raw input value to allow backspace/clearing
        if (inputValue === '') {
          // Store NaN temporarily to indicate empty input
          newValue[index] = NaN;
        } else {
          const numValue = Number(inputValue);
          newValue[index] = isNaN(numValue) ? 0 : numValue;
        }
        handleValueChange(id, newValue);
      }
    },
    [scoreConfig, handleValueChange],
  );

  const handleColorSelect = useCallback(
    (color: string) => {
      if (colorPickerOpen.index !== null) {
        const threshold = scoreConfig[colorPickerOpen.index];
        handleThresholdChange(threshold.id, 'color', color);
      }
      setColorPickerOpen({ index: null, anchorEl: null });
    },
    [colorPickerOpen.index, scoreConfig, handleThresholdChange],
  );

  const openColorPicker = useCallback(
    (index: number, event: React.MouseEvent<HTMLElement>) => {
      setColorPickerOpen({ index, anchorEl: event.currentTarget });
    },
    [],
  );

  // Render functions
  const renderEmptyState = () => (
    <EmptyStateContainer>
      <ScoreConfigIcon className="icon" />
      <Box textAlign="center">
        <Typography className="title">Score Coloring Thresholds</Typography>
        <Typography className="description">
          To customize score color thresholds, first enable Weighted Scoring
          above.
        </Typography>
      </Box>
    </EmptyStateContainer>
  );

  const renderValueInputs = (threshold: Threshold) => {
    const needsTwoInputs = requiresTwoInputs(threshold.logic);

    if (needsTwoInputs) {
      const values = Array.isArray(threshold.value)
        ? threshold.value
        : [0, 100];
      return (
        <>
          <Box>
            <CustomInput
              size="small"
              style={{ marginBottom: 0 }}
              fieldProps={{
                type: 'number',
                value: isNaN(values[0]) ? '' : values?.[0],
                onChange: (e) =>
                  handleRangeValueChange(threshold.id, 0, e.target.value),
                placeholder: '0%',
                inputProps: { max: 100 },
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body2">-</Typography>
          </Box>
          <Box>
            <CustomInput
              size="small"
              style={{ marginBottom: 0 }}
              fieldProps={{
                type: 'number',
                value: isNaN(values[1]) ? '' : values[1],
                onChange: (e) =>
                  handleRangeValueChange(threshold.id, 1, e.target.value),
                placeholder: '100%',
                inputProps: { max: 100 },
              }}
            />
          </Box>
        </>
      );
    } else {
      const singleValue =
        typeof threshold.value === 'string' ? threshold.value : '0';
      return (
        <>
          <Box>
            <CustomInput
              size="small"
              style={{ marginBottom: 0 }}
              fieldProps={{
                type: 'number',
                value: singleValue,
                onChange: (e) =>
                  handleSingleValueChange(threshold.id, e.target.value),
                placeholder: '0%',
                inputProps: { max: 100 },
              }}
            />
          </Box>
          <Box></Box>
          <Box></Box>
        </>
      );
    }
  };

  const renderThresholdRow = (threshold: Threshold, index: number) => {
    const isOverlapping = overlappingIds.has(threshold.id);

    return (
      <ThresholdRowWrapper key={threshold.id} isOverlapping={isOverlapping}>
        <ThresholdRow>
          <ThresholdGrid>
            {/* Color Column */}
            <Box>
              <ColorButton
                onClick={(e) => openColorPicker(index, e)}
                aria-label="Select color"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openColorPicker(index, e);
                  }
                }}
              >
                <ColorPreview color={threshold.color} />
                <ArrowDropDownIcon
                  sx={{
                    color: '#6B7280',
                    fontSize: '20px',
                    marginLeft: 'auto',
                  }}
                />
              </ColorButton>
            </Box>

            {/* Rating Column */}
            <Box>
              <CustomInput
                size="small"
                style={{ marginBottom: 0 }}
                fieldProps={{
                  value: threshold.rating,
                  onChange: (e) =>
                    handleThresholdChange(
                      threshold.id,
                      'rating',
                      e.target.value,
                    ),
                  placeholder: 'Rating',
                }}
              />
            </Box>

            {/* Threshold Type Column */}
            <Box
              sx={{
                '& .MuiFormControl-root': {
                  width: '100%',
                  '& .MuiTypography-root': {
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <CSelect
                value={threshold.logic}
                options={THRESHOLD_TYPE_OPTIONS}
                onChange={(e) =>
                  handleThresholdChange(threshold.id, 'logic', e.target.value)
                }
                size="small"
                placeholder="Select type"
              />
            </Box>

            {/* Dynamic Value Inputs */}
            {renderValueInputs(threshold)}

            {/* Delete Button */}
            <DeleteButtonContainer>
              <IconButton
                size="small"
                onClick={() => handleDeleteThreshold(threshold.id)}
                aria-label="Delete threshold"
                sx={{ color: 'error.main' }}
              >
                <RemoveCircleIcon />
              </IconButton>
            </DeleteButtonContainer>
          </ThresholdGrid>
        </ThresholdRow>
      </ThresholdRowWrapper>
    );
  };

  const renderThresholdsSection = () => (
    <SectionContainer>
      <Typography className="mainTitle">Thresholds</Typography>

      {!isScoring ? (
        renderEmptyState()
      ) : (
        <Box>
          {/* Header Row */}
          <HeaderContainer>
            <ThresholdGrid>
              <Typography className="headerTitle" fontWeight={600}>
                Color
              </Typography>
              <Typography className="headerTitle" fontWeight={600}>
                Rating
              </Typography>
              <Typography className="headerTitle" fontWeight={600}>
                Threshold
              </Typography>
              <Box></Box>
              <Box></Box>
              <Box></Box>
              <Box></Box>
            </ThresholdGrid>
          </HeaderContainer>

          {/* Threshold Rows */}
          {scoreConfig?.map((threshold, index) =>
            renderThresholdRow(threshold, index),
          )}

          {/* Overlap Error Message */}
          {hasOverlaps && (
            <OverlapErrorContainer>
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#dc2626',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Thresholds must have unique, non-overlapping ranges. Please
                adjust the ranges to avoid conflicts.
              </Typography>
            </OverlapErrorContainer>
          )}

          {/* Add Threshold Button */}
          <AddThresholdButtonCnt>
            <CustomButton
              variant="text"
              className="addBtn"
              startIcon={<AddIcon />}
              onClick={handleAddThreshold}
            >
              Add threshold
            </CustomButton>
          </AddThresholdButtonCnt>
        </Box>
      )}
    </SectionContainer>
  );

  return (
    <>
      <CustomDialog
        header={
          <CustomDialogHeader onClose={handleClose}>
            Scoring Configurations
          </CustomDialogHeader>
        }
        dialogProps={{
          open: !!open,
          maxWidth: 'md',
          onClose: handleClose,
          fullWidth: true,
          sx: {
            '& .MuiDialog-paper': { maxWidth: '700px' },
            '& .MuiDialogContent-root': { maxWidth: '100%', width: 700 },
          },
        }}
        actionButtonCntProps={{
          primaryBtnProps: { onClick: handleSave, disabled: !isValid() },
          secondaryBtnProps: { onClick: handleClose },
        }}
        primaryBtnText="Save"
        secondaryBtnText="Cancel"
      >
        <ContentContainer>
          {/* Important Update Notification */}
          {showImportantUpdate && (
            <ImportantUpdateNotification onDismiss={dismissNotification} />
          )}

          {/* Weighted Scoring Section */}
          <SectionContainer>
            <Box
              className="weightedScoreBox"
              sx={{
                ...(isScoring && { backgroundColor: '#EBEAFF !important' }),
              }}
            >
              <Stack direction="row" alignItems="center">
                <IosSwitch
                  checked={isScoring}
                  onChange={handleIsScoringToggle}
                  height={20}
                  width={32}
                  thumbSize={16}
                />
                <Box sx={{ marginLeft: '15px' }}>
                  <Typography className="title">Weighted Scoring</Typography>
                  <Typography className="description">
                    Set individual question scores. Section scores reflect total
                    points earned.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </SectionContainer>

          {/* Thresholds Section */}
          {renderThresholdsSection()}
        </ContentContainer>
      </CustomDialog>

      {/* Color Picker */}
      {colorPickerOpen.anchorEl && (
        <ColorPicker
          anchorEl={colorPickerOpen.anchorEl}
          open={!!colorPickerOpen.anchorEl}
          value={
            colorPickerOpen.index !== null
              ? scoreConfig[colorPickerOpen.index]?.color
              : DEFAULT_COLORS[0]
          }
          onClose={() => setColorPickerOpen({ index: null, anchorEl: null })}
          onSelect={handleColorSelect}
        />
      )}
    </>
  );
};

export default ScoringConfigurations;
